import React, { useState } from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useRole } from '../../hooks/useRole';
import { usePermission } from '../../hooks/usePermission';
import DataTable from '../shared/DataTable';
import RoleSettings from './RoleSettings';
import { EditRolesPermission, ViewRolesPermission, DeleteRolesPermission } from '../../constants/permissions';

const RoleTable = () => {
    const { data: roles, isLoading } = useRole();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedActionId, setSelectedActionId] = useState(null);

    const { data: canUpdateRole } = usePermission({
        permissionName: EditRolesPermission
    });
    const { data: canDeleteRole } = usePermission({
        permissionName: DeleteRolesPermission
    });
    const { data: canViewRole } = usePermission({
        permissionName: ViewRolesPermission
    });

    const handleMenuClick = (event, id) => {
        setAnchorEl(event.currentTarget);
        setSelectedActionId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedActionId(null);
    };

    const columns = [
        {
            name: 'Role Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Description',
            selector: row => row.description || '',
            sortable: true,
        },
        {
            name: 'Permissions',
            selector: row => `${row.permissionsCount || 0} permissions`,
            sortable: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <IconButton onClick={(e) => handleMenuClick(e, row.id)}>
                    <MoreVertIcon />
                </IconButton>
            ),
            width: '100px',
        },
    ];

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <DataTable
                columns={columns}
                data={roles}
                searchFields={['name', 'description']}
                searchPlaceholder="Search roles..."
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {canViewRole && (
                    <MenuItem onClick={() => {
                        const role = roles.find(r => r.id === selectedActionId);
                        if (role) {
                            // Open view permissions dialog
                            const event = new CustomEvent('viewPermissions', { detail: { role } });
                            window.dispatchEvent(event);
                        }
                        handleMenuClose();
                    }}>
                        <i className="fa-solid fa-eye text-blue-600 mr-2" />
                        <h6 className='text-blueColor'>View</h6>
                    </MenuItem>
                )}
                {canUpdateRole && (
                    <MenuItem onClick={() => {
                        const role = roles.find(r => r.id === selectedActionId);
                        if (role) {
                            // Open edit dialog
                            const event = new CustomEvent('editRole', { detail: { role } });
                            window.dispatchEvent(event);
                        }
                        handleMenuClose();
                    }}>
                        <i className="fa-solid fa-edit text-green-700 mr-2 mb-1" />
                        <h6 className='text-blueColor'>Edit</h6>
                    </MenuItem>
                )}
                {canDeleteRole && (
                    <MenuItem onClick={() => {
                        const role = roles.find(r => r.id === selectedActionId);
                        if (role) {
                            // Open delete dialog
                            const event = new CustomEvent('deleteRole', { detail: { role } });
                            window.dispatchEvent(event);
                        }
                        handleMenuClose();
                    }}>
                        <i className="fa-solid fa-trash text-red-600 mr-2" />
                        <h6 className='text-blueColor'>Delete</h6>
                    </MenuItem>
                )}
            </Menu>

            {/* Add RoleSettings component to handle dialogs */}
            <RoleSettings />
        </>
    );
};

export default RoleTable; 